import { useState } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { formatDate } from '@xdocs/i18n';
import type { ContactSubmission } from '@xdocs/schemas';
import { Badge, Button, Modal, Textarea, cn, useI18n } from '@xdocs/ui';
import { requestTyped } from '@xdocs/ws-protocol';
import { safeT } from '../../../i18n/safeT.js';
import { useWs } from '../../../ws/WsProvider.js';
import { useInboxT } from './useInboxT.js';
import styles from './ContactDetailPanel.module.css';

export interface ContactDetailPanelProps {
  submission: ContactSubmission;
  onClose: () => void;
  onHandled: (updated: ContactSubmission) => void;
}

/**
 * F53 detail view — full message + assign/respond/close, each a real WS
 * mutation (`admin.contact.handle`) writing `audit_events`
 * (`action='contact.handle'`). DMCA submissions get a jump-to-Assets hint
 * (F53 AC) — `contact_submissions` carries no structured share/document
 * reference, so this links to `/admin/assets` rather than a deep link (see
 * this task's returned open-issues list).
 */
export function ContactDetailPanel({ submission, onClose, onHandled }: ContactDetailPanelProps): ReactElement {
  const { locale } = useI18n();
  const { t } = useInboxT();
  const { wsClient } = useWs();
  const [replying, setReplying] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [pendingAction, setPendingAction] = useState<'assign' | 'respond' | 'close' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handle(action: 'assign' | 'respond' | 'close'): Promise<void> {
    setPendingAction(action);
    setError(null);
    try {
      const updated = await requestTyped(wsClient, 'admin.contact.handle', {
        submissionId: submission.id,
        action,
        ...(action === 'respond' ? { responseMessage } : {}),
      });
      onHandled(updated);
      if (action === 'respond') {
        setReplying(false);
        setResponseMessage('');
      }
    } catch (err) {
      setError(safeT(locale, (err as { message?: string }).message ?? 'errors:code.INTERNAL'));
    } finally {
      setPendingAction(null);
    }
  }

  return (
    <Modal open onClose={onClose} title={t('adminInbox:detail.title')} size="md" scrollableBody>
      <div className={cn(styles['detail'])}>
        <div className={cn(styles['metaRow'])}>
          <Badge tone={submission.submissionType === 'dmca' ? 'error' : 'info'}>{t(`adminInbox:type.${submission.submissionType}`)}</Badge>
          <Badge tone={submission.status === 'handled' ? 'success' : submission.status === 'in_progress' ? 'warning' : 'neutral'}>
            {t(`adminInbox:status.${submission.status}`)}
          </Badge>
        </div>

        <dl className={cn(styles['fields'])}>
          <div>
            <dt>{t('adminInbox:detail.field.from')}</dt>
            <dd>
              {submission.name} · {submission.email}
            </dd>
          </div>
          {submission.subject && (
            <div>
              <dt>{t('adminInbox:detail.field.subject')}</dt>
              <dd>{submission.subject}</dd>
            </div>
          )}
          <div>
            <dt>{t('adminInbox:detail.field.received')}</dt>
            <dd>{formatDate(locale, submission.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}</dd>
          </div>
        </dl>

        <p className={cn(styles['message'])}>{submission.message}</p>

        {submission.submissionType === 'dmca' && (
          <p className={cn(styles['dmcaHint'])}>
            {t('adminInbox:detail.dmca.hint')}{' '}
            <Link to="/admin/assets">{t('adminInbox:detail.dmca.openAssets')}</Link>
          </p>
        )}

        {error && (
          <p role="alert" className={cn(styles['error'])}>
            {error}
          </p>
        )}

        {replying ? (
          <div className={cn(styles['replyForm'])}>
            <Textarea
              label={t('adminInbox:detail.response.label')}
              placeholder={t('adminInbox:detail.response.placeholder')}
              rows={5}
              value={responseMessage}
              onChange={(event) => setResponseMessage(event.target.value)}
            />
            <div className={cn(styles['actions'])}>
              <Button variant="secondary" onClick={() => setReplying(false)} disabled={pendingAction === 'respond'}>
                {t('adminInbox:detail.action.close')}
              </Button>
              <Button
                variant="primary"
                state={pendingAction === 'respond' ? 'loading' : 'idle'}
                disabled={responseMessage.trim().length === 0}
                onClick={() => void handle('respond')}
              >
                {t('adminInbox:detail.response.send')}
              </Button>
            </div>
          </div>
        ) : (
          <div className={cn(styles['actions'])}>
            <Button
              variant="secondary"
              state={pendingAction === 'assign' ? 'loading' : 'idle'}
              disabled={submission.status !== 'new'}
              {...(submission.status !== 'new' ? { disabledReason: t(`adminInbox:status.${submission.status}`) } : {})}
              onClick={() => void handle('assign')}
            >
              {t('adminInbox:detail.action.assign')}
            </Button>
            <Button variant="secondary" onClick={() => setReplying(true)} disabled={pendingAction !== null}>
              {t('adminInbox:detail.action.respond')}
            </Button>
            <Button
              variant="primary"
              state={pendingAction === 'close' ? 'loading' : 'idle'}
              disabled={submission.status === 'handled'}
              {...(submission.status === 'handled' ? { disabledReason: t('adminInbox:status.handled') } : {})}
              onClick={() => void handle('close')}
            >
              {t('adminInbox:detail.action.close')}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
